import * as React from 'react';
import * as PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import {
  Wrapper,
  Box,
  Input,
  DatePickerIcon,
} from './styled';
import { Icon } from './../Icon';
import { Calendar } from './components/Calendar';
import { DayType } from './types/Day';
import { DateType } from './types/Date';
import { useDate } from './hooks/useDate';
import { getDateByPosition } from './utils';

interface Props {
  name?: string;
  date?: string;
  minDate?: string;
  maxDate?: string;
  width?: string;
  fullWidth?: boolean;
  error?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  uncontrolled?: boolean;
  onChange?: Function;
}

export const DatePicker: React.FC<Props> = ({
  name,
  date = '',
  minDate,
  maxDate,
  width,
  fullWidth,
  error,
  disabled,
  readOnly,
  uncontrolled,
  onChange,
  ...props
}) => {
  const [ open, setOpen ] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  });

  const handleOutsideClick = (e) => {
    const isOutside = !ref.current.contains(e.target);
    isOutside && setOpen(false);
  };

  const [ value, setValue ] = useState(date);
  const { day, month, year }: DateType = useDate(value);

  useEffect(() => {
    uncontrolled && setValue(date);
  }, [date]);

  const handleClick = (day: DayType, month: number, year: number) => {
    const selectedDay = day.dayMonth;
    setValue(getDateByPosition[day.position](selectedDay, month, year));
    setOpen(false);
    onChange && onChange(getDateByPosition[day.position](selectedDay, month, year));
  };

  const handleChangeInput = (e) => {
    const inputVal = e.target.value;
    setValue(inputVal);
  };

  return (
    <Wrapper
      ref={ref}
      width={width}
      fullWidth={fullWidth}
      disabled={disabled}
    >
      <Box>
        <Input
          name={name}
          placeholder="dd/mm/aaaa"
          value={value}
          onClick={() => setOpen(true)}
          onChange={e => handleChangeInput(e)}
          error={error}
          autoComplete="off"
          readOnly={readOnly}
          {...props}
        />
        <DatePickerIcon error={error}>
          <Icon name="calendar" color="#B6B6B6"/>
        </DatePickerIcon>
      </Box>
      { open &&
        <Calendar
          month={month}
          year={year}
          onClick={handleClick}
          selected={day}
          min={minDate}
          max={maxDate}
        />
      }
    </Wrapper>
  );
};

DatePicker.propTypes = {
  name: PropTypes.string,
  date: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  width: PropTypes.string,
  fullWidth: PropTypes.bool,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  uncontrolled: PropTypes.bool,
  onChange: PropTypes.func
}

DatePicker.defaultProps = {
  width: '200px'
}

DatePicker.displayName = 'DatePicker';
